/**
 * RESPONSIBILITY: move a strip of cards, one card at a time.
 *
 * WHAT IS IN THE STRIP IS NOT THIS FILE'S BUSINESS. Events and the core tenets both move
 * the same way and must keep moving the same way; two copies of the rules below would
 * drift the first time one of them was corrected. The caller supplies the cards.
 *
 * A scrollbar is a desktop affordance for a strip that is mostly looked at on a phone,
 * and it says nothing about whether there is more to see. Two buttons say it plainly,
 * and the strip moves on its own so somebody who never touches either still sees what
 * is further along.
 *
 * IT STILL SCROLLS. The track keeps native overflow — only the bar is hidden — so a
 * swipe, a trackpad and the keyboard all keep working and the buttons are one more way
 * in rather than the only one.
 *
 * WHEN IT MOVES, AND WHEN IT DOES NOT:
 *   · a rotating carousel carries a visible control to stop it, and that control is
 *     first in the tab order, because somebody who needs it needs it immediately
 *     (WAI-ARIA Authoring Practices, Carousel);
 *   · rotation is held while one of the controls HAS FOCUS, and starts again the moment
 *     that focus goes. Focus is the honest signal that somebody has taken hold of this
 *     thing; a pointer merely crossing the band is not, and stopping for it stops the
 *     carousel for people who are not using it at all;
 *   · a press or a swipe that takes no focus holds it for a quiet period the church sets
 *     — that is the only case a timer is needed for, and losing focus cuts it short;
 *   · stopping it deliberately is permanent until they start it again. Focus going
 *     elsewhere does not undo a decision;
 *   · `aria-live` is off while it rotates and polite once stopped, so a screen reader is
 *     not read a slide it did not ask for;
 *   · nothing moves at all for somebody who has asked for reduced motion.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { type ReactNode } from 'react';
import { reducedMotion, useIdle } from '../events/carousel-timing';

export interface CarouselProps {
  children: ReactNode;
  /** How many cards are in there. One card never rotates and needs no arrows. */
  count: number;
  /** Seconds each card is shown. Under one, it does not move by itself at all. */
  seconds?: number;
  /** Seconds after a press or a swipe before it starts again. Focus does not wait. */
  resumeAfter?: number;
  prevLabel: string;
  nextLabel: string;
  pauseLabel: string;
  playLabel: string;
  /** Names the row of dots for a screen reader, and each dot by number. */
  dotsLabel?: string;
}

/** How far one press moves: the gap between two cards, or the whole strip if there is one. */
function stepWidth(track: HTMLDivElement): number {
  const kids = track.children;
  if (kids.length > 1) {
    return (kids[1] as HTMLElement).offsetLeft - (kids[0] as HTMLElement).offsetLeft;
  }
  return (kids[0] as HTMLElement | undefined)?.offsetWidth ?? track.clientWidth;
}

export function Carousel({
  children, count, seconds, resumeAfter, prevLabel, nextLabel, pauseLabel, playLabel,
  dotsLabel,
}: CarouselProps) {
  const track = useRef<HTMLDivElement>(null);
  const [stopped, setStopped] = useState(false);
  const [held, setHeld] = useState(false);
  const [at, setAt] = useState(0);
  const [stops, setStops] = useState(1);
  const { idle, touched, settle } = useIdle(resumeAfter);

  const move = useCallback((dir: 1 | -1) => {
    const el = track.current;
    if (!el || el.children.length === 0) return;

    const end = el.scrollWidth - el.clientWidth;
    // Wrap rather than stop, in both directions: a strip that dead-ends on its last card
    // leaves the button it is under doing nothing, which reads as broken.
    if (dir === 1 && el.scrollLeft >= end - 4) el.scrollTo({ left: 0, behavior: 'smooth' });
    else if (dir === -1 && el.scrollLeft <= 4) el.scrollTo({ left: end, behavior: 'smooth' });
    else el.scrollBy({ left: dir * stepWidth(el), behavior: 'smooth' });
  }, []);

  const every = Math.round(seconds ?? 0);
  const rotating =
    !stopped && !held && idle && every >= 1 && count > 1 && !reducedMotion();

  useEffect(() => {
    if (!rotating) return;
    // A hidden tab still fires intervals. Scrolling a strip nobody is looking at only
    // means they come back to a carousel already somewhere in the middle of itself.
    const t = window.setInterval(() => { if (!document.hidden) move(1); }, every * 1000);
    return () => window.clearInterval(t);
  }, [rotating, every, move]);

  /*
   * ONE DOT PER PLACE THE STRIP CAN STOP, not per card — which is not the same number
   * whenever more than one card is on screen. Seven cards three-up scroll to five
   * positions: the strip runs out of travel at scrollWidth - clientWidth, so an index
   * counted as scrollLeft / step tops out at four and the last two dots could never
   * light however far it was scrolled.
   *
   * Read from the strip rather than counted from presses: a swipe, a trackpad and the
   * keyboard move it too, and a tally of button presses drifts from what is on screen.
   */
  useEffect(() => {
    const el = track.current;
    if (!el) return;

    const read = () => {
      const step = stepWidth(el) || 1;
      const max = el.scrollWidth - el.clientWidth;
      const last = max <= 0 ? 0 : Math.round(max / step);
      setStops(last + 1);
      setAt(max <= 0 ? 0 : Math.min(Math.round(el.scrollLeft / step), last));
    };

    read();
    el.addEventListener('scroll', read, { passive: true });
    // How many cards fit changes with the width, and so does the number of stops.
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', read); ro.disconnect(); };
  }, [count]);

  const goTo = (i: number) => {
    const el = track.current;
    if (!el) return;
    touched();
    // Clamped, so the last dot lands on the end of the strip rather than past it.
    const max = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: Math.min(i * stepWidth(el), max), behavior: 'smooth' });
  };

  const press = (dir: 1 | -1) => { touched(); move(dir); };
  const canRotate = every >= 1 && count > 1;

  return (
    <div
      className="carousel"
      role="group" aria-roledescription="carousel"
      // Focus in, focus out — nothing about the pointer. onBlurCapture fires as focus
      // moves BETWEEN two controls in here as well, but the onFocusCapture that follows
      // lands in the same tick, so the strip never twitches between two presses.
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => { setHeld(false); settle(); }}
      onTouchStart={touched}
    >
      {/* First in the tab order, before the cards: a person who needs it to stop needs
          that before they need any of the content it is moving. */}
      {canRotate && (
        <button
          type="button" className="carousel-stop"
          aria-label={stopped ? playLabel : pauseLabel} title={stopped ? playLabel : pauseLabel}
          onClick={() => setStopped(!stopped)}
        >{stopped ? '▶' : '❚❚'}</button>
      )}

      <div className="carousel-track" ref={track} aria-live={rotating ? 'off' : 'polite'}>
        {children}
      </div>

      {/* How many there are, and which one this is. A strip that scrolls with no marker
          says nothing about how much is left; the dots are also a way in for somebody who
          would rather jump than page through. */}
      {stops > 1 && (
        <div className="carousel-dots" role="group" aria-label={dotsLabel || undefined}>
          {Array.from({ length: stops }, (_, i) => (
            <button
              key={i}
              type="button"
              className={i === at ? 'carousel-dot is-at' : 'carousel-dot'}
              aria-label={dotsLabel ? `${dotsLabel} ${i + 1}` : `${i + 1}`}
              aria-current={i === at || undefined}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}

      {count > 1 && (
        <>
          <button type="button" className="carousel-go carousel-prev"
                  aria-label={prevLabel} title={prevLabel}
                  onClick={() => press(-1)}>‹</button>
          <button type="button" className="carousel-go carousel-next"
                  aria-label={nextLabel} title={nextLabel}
                  onClick={() => press(1)}>›</button>
        </>
      )}
    </div>
  );
}
